import { supabase } from './supabase';

interface Ticket {
  id: string;
  title: string;
  external_link?: string;
  sprint_id?: string;
  status: string;
  created_at: string;
  updated_at: string;
  ticket_number: string;
  completion_percentage: number;
}

export async function fetchUserTickets(
  userId: string,
  shadowTo: string | null | undefined,
  options: {
    sprintId: string;
    includeRemoved?: boolean;
  },
): Promise<
  (Omit<Ticket, 'ticket_check_ins'> & { completion_percentage: number })[]
> {
  const userIds = shadowTo ? [userId, shadowTo] : [userId];

  let query = supabase
    .from('tickets')
    .select('*')
    .in('assigned_to', userIds)
    .eq('sprint_id', options.sprintId)
    .order('created_at', { ascending: false });

  if (!options.includeRemoved) {
    query = query.is('removed_at', null);
  }

  const ticketsQuery = await query.returns<Ticket[]>();

  if (ticketsQuery.error) {
    throw new Error(`Failed to fetch tickets: ${ticketsQuery.error.message}`);
  }

  const checkInsQuery = await supabase
    .from('ticket_check_ins')
    .select('ticket_id, completion_percentage, created_at')
    .in(
      'ticket_id',
      ticketsQuery.data.map((ticket) => ticket.id),
    )
    .order('created_at', { ascending: false });

  if (checkInsQuery.error) {
    throw new Error(
      `Failed to fetch check-ins: ${checkInsQuery.error.message}`,
    );
  }

  // Create a map of ticket_id to completion_percentage using only the latest check-in per ticket
  const checkInsMap = new Map();
  checkInsQuery.data.forEach(({ ticket_id, completion_percentage }) => {
    // Only set the value if it hasn't been set before (since results are ordered by created_at DESC)
    if (!checkInsMap.has(ticket_id)) {
      checkInsMap.set(ticket_id, completion_percentage);
    }
  });

  return ticketsQuery.data.map((ticket) => ({
    ...ticket,
    completion_percentage: (checkInsMap.get(ticket.id) as number) ?? 0,
  }));
}
